import get from 'lodash/get';

class ProductGroups {
  /**
   * Initialize a new instance of ProductGroups class.
   * @param data
   * @param options
   */
  constructor(data, options = {}) {
    this.data = data;
    if (!data || !data.variants || !data.categorizationHierarchyMetadata || !data.categorizationHierarchyValues) {
      throw new Error('Product data is invalid.');
    }

    this.options = Object.assign(this.getDefaultOptions(), options);
    this.productsMap = this.getProductsMap();
    this.images = this.getPrimaryImages();
    this.hierarchyMeta = this.getCategorizationMeta();
    this.hierarchy = data.categorizationHierarchyValues;
  }

  /**
   * Get default options.
   */
  getDefaultOptions() {
    return {
      isSkipArchiveProducts: true,
    };
  }

  /**
   * Get categorization meta as an object where key is meta's code.
   * @return {{}}
   */
  getCategorizationMeta() {
    const meta = {};
    this.data.categorizationHierarchyMetadata.forEach((item) => {
      meta[item.code] = item;
    });
    return meta;
  }

  /**
   * Get a list of primary images.
   * @return {{}}
   */
  getPrimaryImages() {
    const images = {};
    for (let image in this.data.images) {
      const primary = this.data.images[image].reduce((prev, curr) => (prev.weight < curr.weight ? prev : curr));
      images[image] = primary ? primary.assetId : '';
    }
    return images;
  }

  /**
   * Initialize object with a list of variants where variant's code is a key.
   * @return {{}}
   */
  getProductsMap() {
    const map = {};
    this.data.variants.forEach((variant) => {
      map[variant.code] = Object.assign(
        {
          skuQtyInSavedList: '',
          skuQtyInCart: '',
        },
        variant
      );
    });

    return map;
  }

  /**
   * Get categorization value for an item in name / value format.
   * @param value
   * @return {{name: *, value: *}}
   */
  getCategorizationForItem(value) {
    return {
      name: get(this.hierarchyMeta[value.code], 'options.displayName'),
      value: get(value, 'options.displayName'),
    };
  }

  /**
   * Sort items by weight.
   * @param items
   * @return {*}
   */
  sortByWeight(items) {
    return items.sort((a, b) => a.weight - b.weight);
  }

  /**
   * Get list of products with categorization for a given group.
   * @param group
   * @param hasChildren
   * @return {[]}
   */
  getProductsForGroup(group, hasChildren) {
    let categorization = [];
    const getProducts = (parent) => {
      let products = [];
      let isLastLevel = false;
      this.sortByWeight(parent).forEach((child) => {
        if (child.children && child.children.length > 0) {
          categorization.push(this.getCategorizationForItem(child));
          products = products.concat(getProducts(child.children));
        } else {
          const code = child.options.productCode;
          if (!this.productsMap[code] || (this.options.isSkipArchiveProducts && this.productsMap[code].isArchived)) {
            categorization = [];
            return;
          }

          products.push({
            ...this.productsMap[code],
            categorization: hasChildren ? [...categorization, this.getCategorizationForItem(child)] : [],
          });

          isLastLevel = true;
        }
      });

      if (isLastLevel) {
        categorization = [];
      }

      return products;
    };

    return getProducts(group);
  }

  /**
   * Get general data which is needed for rendering group.
   * @return {{code: *, name: *, productType: *, marketingModelYear: *}}
   */
  getGroupData() {
    return {
      code: this.data.code,
      productType: this.data.productType,
      name: this.data.name,
      marketingModelYear: this.data.marketingModelYear,
      packageQty: parseInt(this.data.packageQty),
      totalShipmentNumber: this.data.totalShipmentNumber,
      isPreSeasonImmediateOrder: this.data.isPreseasonImmediateOrder,
    };
  }

  /**
   * Get grouped products.
   * @return {*}
   */
  getProductGroups() {
    const productGroups = [];
    this.hierarchy.forEach((parent) => {
      const hasChildren = parent.children && parent.children.length > 0;
      const products = this.getProductsForGroup(!hasChildren ? [parent] : parent.children, hasChildren);
      const modelYearCurrent = this.getCurrentModelYear(products);
      if (!products || !products.length) {
        return;
      }

      productGroups.push({
        group: {
          meta: this.hierarchyMeta[parent.code],
          value: parent,
          image: get(this.images, parent.options.colorSwatchName, ''),
        },
        modelYearCurrent,
        products,
        ...this.getGroupData(),
      });
    });

    return productGroups;
  }

  getCurrentModelYear(products) {
    const variantYearList = [];
    let currentModelYear = '';

    const getModelYear = (products) => {
      products.forEach((product) => {
        variantYearList.push(product.marketingModelYear);
      });
      variantYearList.sort();
      let uniqueYear = [...new Set(variantYearList)];

      if (uniqueYear.length > 1) {
        currentModelYear = uniqueYear[uniqueYear.length - 1];
        return currentModelYear;
      } else {
        return (currentModelYear = uniqueYear[0]);
      }
    };
    return getModelYear(products);
  }
}

export default ProductGroups;
